import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BanknotesIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  XMarkIcon,
  ReceiptPercentIcon
} from '@heroicons/react/24/outline'
import cashfreeService from '../../services/cashfreeService'
import { supabase } from '../../config/supabase'
import toast from 'react-hot-toast'

const StaffPaymentCollection = ({ 
  isOpen, 
  onClose, 
  orderData, 
  staffId, 
  onPaymentCollected 
}) => {
  const [selectedMethod, setSelectedMethod] = useState('cash')
  const [collecting, setCollecting] = useState(false)
  const [tipAmount, setTipAmount] = useState(0)
  const [showTipInput, setShowTipInput] = useState(false)

  // Calculate totals
  const orderTotal = orderData?.total_amount || 0
  const finalTotal = orderTotal + tipAmount

  const paymentMethods = [
    {
      id: 'cash',
      name: 'Cash Payment',
      icon: BanknotesIcon,
      description: 'Customer pays with cash',
      color: 'green'
    },
    {
      id: 'card',
      name: 'Card Payment',
      icon: CreditCardIcon,
      description: 'Customer pays with card',
      color: 'blue'
    },
    {
      id: 'upi',
      name: 'UPI Payment',
      icon: DevicePhoneMobileIcon,
      description: 'Customer pays via UPI',
      color: 'purple'
    }
  ]

  const handleCollectPayment = async () => {
    if (!orderData || !staffId) {
      toast.error('Missing order or staff information')
      return
    }

    setCollecting(true)

    try {
      console.log('💰 Staff collecting payment:', {
        orderId: orderData.id,
        method: selectedMethod,
        amount: orderTotal,
        tip: tipAmount,
        total: finalTotal
      })

      // Use Cashfree service to record staff payment collection
      const collectionResult = await cashfreeService.collectPaymentByStaff({
        orderId: orderData.id,
        staffId: staffId,
        collectionMethod: selectedMethod,
        amount: orderTotal,
        tipAmount: tipAmount
      })

      if (!collectionResult.success) {
        throw new Error(collectionResult.error || 'Payment collection failed')
      }

      // Update order status to completed and payment collected
      const { error: orderUpdateError } = await supabase
        .from('orders')
        .update({
          status: 'completed',
          payment_status: 'completed',
          payment_method: selectedMethod,
          tip_amount: tipAmount,
          completed_at: new Date().toISOString(),
          staff_collection_data: {
            collected_by: staffId,
            collection_method: selectedMethod,
            collection_time: new Date().toISOString(),
            tip_amount: tipAmount,
            total_collected: finalTotal
          }
        })
        .eq('id', orderData.id)

      if (orderUpdateError) {
        console.error('❌ Error updating order:', orderUpdateError)
        throw new Error('Failed to update order status')
      }

      // Record staff earnings (tip + commission if applicable)
      const staffEarnings = {
        staff_id: staffId,
        order_id: orderData.id,
        tip_amount: tipAmount,
        collection_bonus: selectedMethod === 'cash' ? 5 : 0, // ₹5 bonus for cash collection
        total_earnings: tipAmount + (selectedMethod === 'cash' ? 5 : 0),
        collection_method: selectedMethod,
        earned_at: new Date().toISOString()
      }

      const { error: earningsError } = await supabase
        .from('staff_earnings')
        .insert(staffEarnings)

      if (earningsError) {
        console.warn('⚠️ Could not record staff earnings:', earningsError)
        // Don't fail the payment collection for this
      }

      // Calculate platform revenue
      const platformRevenue = cashfreeService.calculatePlatformRevenue(
        orderTotal, 
        0, // No loyalty points in staff collection
        tipAmount
      )

      console.log('📊 Platform Revenue Calculation:', platformRevenue)

      toast.success(
        `Payment collected successfully! ${tipAmount > 0 ? `Tip: ₹${tipAmount}` : ''}`,
        {
          icon: '💰',
          duration: 4000
        }
      )

      // Call parent callback
      if (onPaymentCollected) {
        onPaymentCollected({
          orderId: orderData.id,
          paymentMethod: selectedMethod,
          amount: orderTotal,
          tipAmount: tipAmount,
          totalCollected: finalTotal,
          staffEarnings: staffEarnings,
          platformRevenue: platformRevenue
        })
      }

      onClose()

    } catch (error) {
      console.error('❌ Payment collection error:', error)
      toast.error(`Payment collection failed: ${error.message}`)
    } finally {
      setCollecting(false)
    }
  }

  const getMethodColor = (method) => {
    const colors = {
      cash: 'green',
      card: 'blue',
      upi: 'purple'
    }
    return colors[method] || 'gray'
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Collect Payment
                </h3>
                <p className="text-sm text-gray-600">
                  Order #{orderData?.order_number}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="p-6 border-b bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Order Total</span>
              <span className="text-lg font-bold text-gray-900">₹{orderTotal.toFixed(2)}</span>
            </div>
            
            {tipAmount > 0 && (
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Tip</span>
                <span className="text-lg font-bold text-green-600">₹{tipAmount.toFixed(2)}</span>
              </div>
            )}
            
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="text-base font-bold text-gray-900">Total to Collect</span>
              <span className="text-xl font-bold text-orange-600">₹{finalTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="p-6">
            <h4 className="text-sm font-bold text-gray-900 mb-4">Payment Method</h4>
            <div className="space-y-3">
              {paymentMethods.map((method) => {
                const IconComponent = method.icon
                const isSelected = selectedMethod === method.id
                const colorClass = getMethodColor(method.id)
                
                return (
                  <button
                    key={method.id}
                    onClick={() => setSelectedMethod(method.id)}
                    className={`w-full p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                      isSelected 
                        ? `border-${colorClass}-500 bg-${colorClass}-50` 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? `bg-${colorClass}-100` : 'bg-gray-100'
                    }`}>
                      <IconComponent className={`w-6 h-6 ${
                        isSelected ? `text-${colorClass}-600` : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm font-semibold text-gray-900">
                        {method.name}
                      </div>
                      <div className="text-xs text-gray-600">
                        {method.description}
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? `border-${colorClass}-500` : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <div className={`w-3 h-3 rounded-full bg-${colorClass}-500`}></div>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>

            {/* Tip Section */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-gray-900">Customer Tip (Optional)</h4>
                <button
                  onClick={() => setShowTipInput(!showTipInput)}
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                >
                  {showTipInput ? 'Hide' : 'Add Tip'}
                </button>
              </div>

              {showTipInput && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-3"
                >
                  {/* Quick Tip Amounts */}
                  <div className="grid grid-cols-4 gap-2">
                    {[10, 20, 50, 100].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setTipAmount(amount)}
                        className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          tipAmount === amount
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        ₹{amount}
                      </button>
                    ))}
                  </div>

                  {/* Custom Tip Input */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">₹</span>
                    <input
                      type="number"
                      value={tipAmount}
                      onChange={(e) => setTipAmount(Number(e.target.value) || 0)}
                      placeholder="Custom amount"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    {tipAmount > 0 && (
                      <button
                        onClick={() => setTipAmount(0)}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-6 border-t bg-gray-50">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCollectPayment}
                disabled={collecting}
                className="flex-1 py-3 px-4 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {collecting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Collecting...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-5 h-5" />
                    Collect ₹{finalTotal.toFixed(2)}
                  </>
                )}
              </button>
            </div>

            {/* Platform Commission Info */}
            <div className="mt-4 p-3 bg-white rounded-lg border">
              <div className="text-xs text-gray-600 mb-1">Platform Revenue (3.5%):</div>
              <div className="text-xs text-gray-700">
                Commission: ₹{(orderTotal * 0.035).toFixed(2)} • Restaurant keeps: ₹{(orderTotal * 0.965).toFixed(2)}
              </div>
              {tipAmount > 0 && (
                <div className="text-xs text-green-600 mt-1">
                  Your tip: ₹{tipAmount.toFixed(2)} + Collection bonus: ₹{selectedMethod === 'cash' ? '5' : '0'}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default StaffPaymentCollection
